import {
    createContext,
    ReactElement,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    clearAuthenticated,
    getAuthChangeEventName,
    getAuthEmail,
    getAuthToken,
    isAuthenticated,
} from "../utils/authSession";
import { readIsAdminFromToken } from "../utils/jwt";

export interface AuthenticatedUser {
    email: string | null;
    name: string | null;
    is_admin: boolean;
}

interface AuthContextValue {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: AuthenticatedUser | null;
    refreshAuth: () => void;
    setSessionUser: (payload: { email?: string; name?: string; is_admin?: boolean }) => void;
    signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function buildUserFromSession(
    overrides?: { email?: string; name?: string; is_admin?: boolean },
): AuthenticatedUser | null {
    if (!isAuthenticated()) {
        return null;
    }

    const token = getAuthToken();
    const email = overrides?.email ?? getAuthEmail();

    return {
        email: email ?? null,
        name: overrides?.name ?? null,
        is_admin: overrides?.is_admin ?? readIsAdminFromToken(token),
    };
}

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<AuthenticatedUser | null>(() => buildUserFromSession());

    const refreshAuth = useCallback(() => {
        setUser(buildUserFromSession());
    }, []);

    const setSessionUser = useCallback((payload: { email?: string; name?: string; is_admin?: boolean }) => {
        setUser(buildUserFromSession(payload));
    }, []);

    const signOut = useCallback(() => {
        clearAuthenticated();
        setUser(null);
    }, []);

    useEffect(() => {
        refreshAuth();
        setIsLoading(false);

        const authEventName = getAuthChangeEventName();
        const onAuthChange = () => refreshAuth();

        window.addEventListener(authEventName, onAuthChange);
        window.addEventListener("storage", onAuthChange);

        return () => {
            window.removeEventListener(authEventName, onAuthChange);
            window.removeEventListener("storage", onAuthChange);
        };
    }, [refreshAuth]);

    const value = useMemo(
        () => ({
            isAuthenticated: Boolean(user) || isAuthenticated(),
            isLoading,
            user,
            refreshAuth,
            setSessionUser,
            signOut,
        }),
        [isLoading, refreshAuth, setSessionUser, signOut, user],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }

    return context;
}
